import { useAuthContext } from '@/features/auth/context'
import useUser from '@/features/auth/hooks/useUser'
import { useEffect, type ReactElement } from 'react'
import toast from 'react-hot-toast'
import { Navigate, useLocation } from 'react-router-dom'
import InternalServer from '../common/InternalServer'
import LoadingScreen from '../common/LoadingScreen'
import { useAppDispatch } from '@store/stores'
import { setUser, fetchUserPermissions } from '@store/slices/user/userSlice'

interface RequireIsLoggedInProps {
  roles: string[]
  children: ReactElement
  redirectTo: string
}

const RequireIsLoggedIn = ({
                             roles,
                             redirectTo,
                             children,
                           }: RequireIsLoggedInProps) => {
  const { isLogin, interfaceId } = useAuthContext()
  const { pathname: from } = useLocation()
  const { data: user, isLoading, isError } = useUser()
  const isIncludes = roles.includes(user?.role)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (isIncludes && !isIncludes) toast.error('Permission denied!')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIncludes, isLoading])

  useEffect(() => {
    if (user) {
      dispatch(setUser(user))
    }
  }, [user, dispatch])

  /**
   * @todo: maybe find better place to fetch permissions
   */
  useEffect(() => {
    if (user && interfaceId && !isLoading) {
      dispatch(fetchUserPermissions(Number(interfaceId)))
        .unwrap()
        .catch((error) => {
          console.error('Failed to fetch user permissions:', error)
        })
    }
  }, [user, interfaceId, isLoading, dispatch])

  if (!isLogin) return <Navigate replace to={'/'} />

  if (isLoading) {
    return <LoadingScreen />
  }

  // The user bootstrap fetch (GET /api/users/me) failed — e.g. the identity
  // server is unreachable. Render a terminal error instead of falling through
  // to <Navigate redirectTo>, which would ping-pong between guarded routes and
  // hammer /users/me in an infinite loop.
  if (isError) {
    return <InternalServer />
  }

  if (isIncludes) {
    return children
  }

  return <Navigate replace to={redirectTo} state={{ from }} />
}

export default RequireIsLoggedIn
